import { Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import * as React from 'react';

import { CREATE, DataAPIContext, GET_ONE } from '../../api';
import RemoteAutocomplete from '../../components/RemoteAutocomplete';

const formatUser = (user) => {
    if (!user) {
        return '(no owner)';
    }
    const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
    const label = user.display_name || name || user.email || `User #${user.id}`;
    return user.email && label !== user.email ? `${label} <${user.email}>` : label;
};

const formatStart = (ts) => {
    if (!ts) return null;
    try {
        const d = new Date(ts);
        if (Number.isNaN(d.getTime())) return ts;
        return d.toLocaleString();
    } catch (_) {
        return ts;
    }
};

const BatchChangeBookingOwnerDialog = ({ open, onClose, selectedIds, onChanged }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const { enqueueSnackbar } = useSnackbar();
    const [target, setTarget] = React.useState(null);
    const [submitting, setSubmitting] = React.useState(false);
    const [booking, setBooking] = React.useState(null);
    const [loadingBooking, setLoadingBooking] = React.useState(false);

    const count = selectedIds?.length || 0;
    const singleId = count === 1 ? selectedIds[0] : null;

    React.useEffect(() => {
        if (!open) {
            setTarget(null);
            setSubmitting(false);
            setBooking(null);
            return;
        }
        if (!singleId) {
            setBooking(null);
            return;
        }
        let cancelled = false;
        setLoadingBooking(true);
        dataAPI(GET_ONE, 'tour_user', { id: singleId })
            .then((res) => {
                if (cancelled) return;
                setBooking(res?.data || null);
            })
            .catch(() => {
                if (cancelled) return;
                setBooking(null);
            })
            .finally(() => {
                if (!cancelled) setLoadingBooking(false);
            });
        return () => { cancelled = true; };
    }, [open, singleId, dataAPI]);

    const canSubmit = !!target?.id && !submitting && count > 0
        && (!booking || target.id !== booking.user?.id);

    const handleConfirm = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            const res = await dataAPI(CREATE, 'tour_user', {
                data: {
                    action: 'switch_user',
                    data: { list: selectedIds, target_user_id: target.id },
                },
                urlSuffix: 'batch/',
            });
            const body = res?.data || {};
            const switched = body.switched?.length ?? 0;
            const skipped = body.skipped?.length ?? 0;
            const errors = body.errors || [];
            const summary = [
                `${switched} switched`,
                skipped ? `${skipped} unchanged` : null,
                errors.length ? `${errors.length} failed` : null,
            ].filter(Boolean).join(', ');
            enqueueSnackbar(`Booking owner: ${summary}`, {
                variant: errors.length ? 'warning' : 'success',
            });
            onChanged && onChanged(body);
            onClose();
        } catch (err) {
            const msg = err?.body?.error || err?.message || 'Failed to change booking owner';
            enqueueSnackbar(msg, { variant: 'error' });
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Change owner — {count === 1 ? '1 booking' : `${count} bookings`}</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    Move the selected booking{count === 1 ? '' : 's'} to a different user.
                    Chat membership and per-user manifest will be reconciled. This action is logged.
                </DialogContentText>
                <Stack spacing={2}>
                    {singleId && (
                        <div>
                            <Typography variant="caption" color="text.secondary">Selected booking</Typography>
                            {loadingBooking && !booking && <Typography variant="body2">Loading…</Typography>}
                            {booking && (
                                <Stack spacing={0.25}>
                                    <Typography variant="body2"><strong>Guest:</strong> {formatUser(booking.user)}</Typography>
                                    {booking.start_ts && (
                                        <Typography variant="body2"><strong>Start:</strong> {formatStart(booking.start_ts)}</Typography>
                                    )}
                                    {booking.custom_id && (
                                        <Typography variant="body2"><strong>Booking ref:</strong> {booking.custom_id}</Typography>
                                    )}
                                    {booking.group_tour?.name && (
                                        <Typography variant="body2"><strong>Tour:</strong> {booking.group_tour.name}</Typography>
                                    )}
                                </Stack>
                            )}
                        </div>
                    )}
                    {!singleId && count > 0 && (
                        <Alert severity="info" variant="outlined">
                            {count} bookings will be reassigned to the selected user. Bookings owned by users
                            outside the target's tour operator will be skipped.
                        </Alert>
                    )}
                    <RemoteAutocomplete
                        resource="user"
                        urlSuffix={null}
                        label="New owner"
                        variant="filled"
                        size="small"
                        getOptionLabel={(opt) => {
                            if (!opt) return '';
                            return formatUser(opt);
                        }}
                        value={target}
                        onChange={(_ev, value) => setTarget(value)}
                        disabled={submitting}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>Cancel</Button>
                <Button onClick={handleConfirm} variant="contained" color="primary" disabled={!canSubmit}>
                    {submitting ? 'Working…' : `Change owner`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BatchChangeBookingOwnerDialog;
